import React, { useState } from 'react'
import {useUserProfileContext} from '../../../context/UserProfileContext'
import {useHistory} from 'react-router-dom'
import { defaultProfileImage } from '../../../utils/constants'
import { GenericDropdown } from '../../../hooks/utils/GenericDropdown'
import { activateDeactivateAdminMember } from '../../../services/profileAdminService'
import { useConfirmAction } from '../../../hooks/utils/useConfirmAction'
import { showErrorAlert } from '../../../helpers/alert'
import { resendEmailActivationAccount } from '../../../services/membersAdminService'
import { handleApiError } from '../../../utils/handleApiError'
import { AddMembershipModal } from './modals/AddMembershipModal'

export interface Member {
  id: string
  name: string
  isActive: boolean
  avatar: string
  email: string
  memberSince: string
  plan: string
  assignedTo: string
  assignedToAvatar?: string
}

interface MembersTableProps {
  members: Member[]
  onRowClick?: (id: string) => void
  page: number
  limit: number
  refresh?: () => void
}

export const ManageMemberGrid: React.FC<MembersTableProps> = ({members, onRowClick, page, limit, refresh}) => {
  const [showMembershipModal,setShowMembershipModal] = useState(false)
  const [memberSelected,setMemberSelected] = useState(0)
  const {setSearchParams} = useUserProfileContext()
  const navigate = useHistory()
  const confirm = useConfirmAction()
  const handleClickCell = (memberId: string) => {
    setSearchParams({userSelected: memberId})

    navigate.push('/profile-member')
  }

  const onDelete = async (id:number,isActive?: boolean) => {
    let confirmed = await confirm({
      title: `Confirm ${isActive ? 'deactivate' : 'activate'}`,
      message: `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this member?`,
      btnTextAction: 'Yes, Confirm',
      btnTextDecline: 'Cancel',
    })
    if(confirmed){
      let response = await activateDeactivateAdminMember(id,!isActive)
      showErrorAlert({ title: 'Member Updated', message: 'The member has been successfully updated.' })
      refresh()
    }
  }
  
  const onResendEmail = async (id:number,isActive?: boolean) => {
    if(isActive){
      showErrorAlert({ title: 'Action Not Allowed', message: 'The activation email cannot be resent because this account has already been activated.' })
      return;
    }
    let confirmed = await confirm({
      title: `Confirm Email Resend`,
      message: `This will send a new account activation email to the member. Do you want to continue?`,
      btnTextAction: 'Yes, Resend',
      btnTextDecline: 'Cancel',
    })
    if(confirmed){
      try {
        let response = await resendEmailActivationAccount({ user_id: id })
        if(response?.success){
          showErrorAlert({ title: 'Member Updated', message: response?.message ?? 'The activation email has been resent successfully.' })
        }else{
          showErrorAlert({ title: 'Member Updated', message: response?.message ?? 'The activation email could not be resent. Please try again later.' })
        }
        
      } catch (error) {
        handleApiError(error, {});
      }
    }
  }

  const onCreatePlan = async (id:number) => {
    setMemberSelected(id)
    setShowMembershipModal(true)
  }

  return (
    <div className='members-table'>
      <div className='members-table__header-row'>
        <div className='members-table__header'>ID</div>
        <div className='members-table__header'>name</div>
        <div className='members-table__header'>Member since</div>
        <div className='members-table__header'>Plan</div>
        <div className='members-table__header'>assigned to</div>
        <div className='members-table__header'>status</div>
        <div className='members-table__header'>Actions</div>
      </div>

      {members.map((member,index) => (
        <div
          key={member.id}
          className='members-table__row'
        >
          <div className='members-table__cell' onClick={() => handleClickCell(member.id)}>{(page - 1) * limit + index + 1}</div>
          <div className='members-table__cell card-profile-avatar' onClick={() => handleClickCell(member.id)}>
            <img src={member.avatar || defaultProfileImage} onError={(e) => { e.currentTarget.src = defaultProfileImage }} alt={member.name} />
            <span>{member.name}</span>
          </div>
          <div className='members-table__cell' onClick={() => handleClickCell(member.id)}>{member.memberSince}</div>
          <div className='members-table__cell' onClick={() => handleClickCell(member.id)}>{member.plan}</div>
          <div className='members-table__cell card-profile-avatar' onClick={() => handleClickCell(member.id)}>
            <img src={member?.assignedToAvatar || defaultProfileImage} onError={(e) => { e.currentTarget.src = defaultProfileImage }} alt={member?.assignedTo} />
            <span>{member.assignedTo}</span>
          </div>
          <div className='members-table__cell' onClick={() => handleClickCell(member.id)}>{member.isActive ? 'Active' : 'Inactive'}</div>
          <div className='members-table__cell'>
            <GenericDropdown
              options={[
                {
                  id: 1,
                  label: member.isActive ? 'Deactivate' : 'Activate',
                  onClick: () => {
                    onDelete?.(Number(member.id),member.isActive)
                  }
                },
                {
                  id: 2,
                  label: 'Resend activation email',
                  onClick: () => {
                    onResendEmail?.(Number(member.id),member.isActive)
                  }
                },
                {
                  id: 3,
                  label: 'Add membership',
                  onClick: () => {
                    if(member.plan == 'No Plan'){
                      onCreatePlan?.(Number(member.id))
                    }else{
                      showErrorAlert({ title: 'Action Not Allowed', message: 'This user already has an active membership.' })
                    }
                  }
                },
              ]}
              placeholder="Actions"
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.id}
            />
          </div>
        </div>
      ))}
      {memberSelected>0 && <AddMembershipModal show={showMembershipModal} onHide={()=>{setShowMembershipModal(false);refresh();}} userId={memberSelected} />}
    </div>
  )
}
